/**
 * Programs config matching mobile (personas.json) structure.
 * Used by admin to group and display programs in the same order as signup/onboarding.
 * catalogId = program doc id in Firestore (programs/ or programTemplates/)
 */
import personasData from './personas.json';

const PERSONAS = personasData?.personas || [];

/** Category order matching mobile */
const CATEGORY_ORDER = ['functional_strength', 'longevity', 'athlete', 'rehab_recovery', 'other'];

/** Programs not in personas (admin-only catalogs, runtime templates) */
const EXTRA_PROGRAMS = [
  { catalogId: 'beginner_strength', displayName: 'Beginner Strength', category: 'other' },
  { catalogId: 'sam-multi-sport-sprint-triathlon-2026', displayName: 'Multi-Sport Sprint (Triathlon) — Template', category: 'athlete' },
];

/**
 * Builds program config from personas.
 * @returns {Array<{ catalogId: string, displayName: string, category: string, categoryLabel: string }>}
 */
export function getProgramsConfig() {
  const fromPersonas = [];
  for (const persona of PERSONAS) {
    const category = persona.id;
    const categoryLabel = persona.name || category;
    for (const prog of persona.programs || []) {
      const catalogId = prog.programId || prog.id;
      const displayName = prog.name || catalogId;
      fromPersonas.push({ catalogId, displayName, category, categoryLabel });
    }
  }

  const combined = [...fromPersonas];
  for (const extra of EXTRA_PROGRAMS) {
    if (!combined.some((p) => p.catalogId === extra.catalogId)) {
      const persona = PERSONAS.find((p) => p.id === extra.category);
      combined.push({
        catalogId: extra.catalogId,
        displayName: extra.displayName,
        category: extra.category,
        categoryLabel: persona?.name || 'Other',
      });
    }
  }

  return combined;
}

/**
 * Get category label by id.
 */
export function getCategoryLabel(categoryId) {
  const p = PERSONAS.find((p) => p.id === categoryId);
  return p?.name || (categoryId === 'other' ? 'Other' : categoryId);
}

/**
 * Group programs by category in mobile order.
 * @param {Array<{ id: string, name: string, type?: string, published?: boolean }>} programList
 * @returns {Array<{ category: string, categoryLabel: string, programs: Array }>}
 */
export function groupProgramsByCategory(programList) {
  const config = getProgramsConfig();
  const configByCatalogId = config.reduce((acc, c) => {
    acc[c.catalogId] = c;
    return acc;
  }, {});

  const byCategory = {};
  for (const p of programList) {
    const cfg = configByCatalogId[p.id];
    const category = cfg?.category || 'other';
    const categoryLabel = cfg?.categoryLabel || getCategoryLabel('other');
    if (!byCategory[category]) {
      byCategory[category] = { category, categoryLabel, programs: [] };
    }
    byCategory[category].programs.push({
      ...p,
      displayName: p.displayName || p.name || cfg?.displayName || p.id,
    });
  }

  return CATEGORY_ORDER.filter((c) => byCategory[c]).map((c) => byCategory[c]);
}
